import { Box, Stack } from '@mantine/core'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { pickBoggleChallengeCatalogFromRollSeed, resolveBoggleChallengeRun } from '~/boggle/challenges'
import { getBoggleGameModeDefinition } from '~/boggle/gameModes'
import { getBoggleProgressRepository } from '~/boggle/progress/webProgressRepository'
import { captureBoggleChallengeRevealed } from '~/boggle/telemetry/boggleTelemetry'
import { ChallengeRevealPageHeader } from '~/boggle/ui/challenge/ChallengeRevealPageHeader'
import { ChallengeStagingScreen } from '~/boggle/ui/challenge/ChallengeStagingScreen'
import { RunScreen } from '~/boggle/ui/run/RunScreen'
import { useBoggleModeSession } from '~/boggle/ui/state/ModeSession'

export const Route = createFileRoute('/challenge' as Parameters<typeof createFileRoute>[0])({
  head: () => ({
    meta: [
      { title: 'Challenge — Patter' },
      {
        name: 'description',
        content: 'Play the daily challenge',
      },
    ],
  }),
  component: ChallengeRoute,
})

function ChallengeRoute() {
  const navigate = useNavigate()
  const {
    challengePhase,
    challengeRollSeed,
    clearRouteHostedGameMode,
    newChallengeGame,
    setChallengePhase,
  } = useBoggleModeSession()

  useLayoutEffect(() => {
    clearRouteHostedGameMode()
  }, [clearRouteHostedGameMode])

  const { configurationId: challengeConfigurationId, gameModeId: challengeGameModeId } =
    pickBoggleChallengeCatalogFromRollSeed(challengeRollSeed)
  const [challengeBestScore, setChallengeBestScore] = useState<null | number>(null)
  const resolvedDailyChallenge = resolveBoggleChallengeRun(challengeRollSeed)
  const revealedTelemetrySignatureRef = useRef<string | null>(null)

  useEffect(() => {
    if (challengePhase !== 'reveal') {
      return
    }

    let cancelled = false
    void getBoggleProgressRepository()
      .getChallengeBestScore(challengeGameModeId, challengeRollSeed)
      .then(bestScore => {
        if (!cancelled) {
          setChallengeBestScore(bestScore)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setChallengeBestScore(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [challengeGameModeId, challengePhase, challengeRollSeed])

  useEffect(() => {
    if (challengePhase !== 'reveal') {
      return
    }

    const signature = `${challengeGameModeId}:${challengeRollSeed}:${challengeConfigurationId}`
    if (revealedTelemetrySignatureRef.current === signature) {
      return
    }

    revealedTelemetrySignatureRef.current = signature
    captureBoggleChallengeRevealed({
      aceScore: resolvedDailyChallenge.aceScore,
      challengeConfigurationId,
      challengeProgressSeed: challengeRollSeed,
      gameModeId: challengeGameModeId,
      targetScore: resolvedDailyChallenge.targetScore,
    })
  }, [
    challengeConfigurationId,
    challengeGameModeId,
    challengePhase,
    challengeRollSeed,
    resolvedDailyChallenge.aceScore,
    resolvedDailyChallenge.targetScore,
  ])

  let content = (
    <ChallengeStagingScreen
      gameMode={getBoggleGameModeDefinition(challengeGameModeId)}
      onBack={() => void navigate({ to: '/' })}
      onPractice={() => setChallengePhase('practice')}
      onStart={() => setChallengePhase('run')}
      resolvedRun={resolvedDailyChallenge}
      seed={challengeRollSeed}
    />
  )

  if (challengePhase === 'run') {
    content = (
      <RunScreen
        challengeConfigurationId={challengeConfigurationId}
        gameModeId={challengeGameModeId}
        onBackToChallengeStaging={() => setChallengePhase('reveal')}
        onExitRun={() => setChallengePhase('reveal')}
        onNewGame={() => newChallengeGame()}
        runKind="challenge"
        seed={challengeRollSeed}
      />
    )
  } else if (challengePhase === 'practice') {
    content = (
      <RunScreen
        key={`practice-${challengeGameModeId}-${challengeRollSeed}`}
        challengeConfigurationId={challengeConfigurationId}
        gameModeId={challengeGameModeId}
        onBackToChallengeStaging={() => setChallengePhase('reveal')}
        onExitRun={() => setChallengePhase('reveal')}
        onNewGame={() => {}}
        runKind="challenge-practice"
        seed={challengeRollSeed}
      />
    )
  }

  return (
    <Stack gap={0} h="100%" mih={0}>
      {challengePhase === 'reveal' ? (
        <ChallengeRevealPageHeader
          aceScore={resolvedDailyChallenge.aceScore}
          bestScore={challengeBestScore}
          targetScore={resolvedDailyChallenge.targetScore}
        />
      ) : null}
      <Box flex={1} mih={0}>
        {content}
      </Box>
    </Stack>
  )
}
